import { IHttpService, IPromise, IRequestShortcutConfig } from "angular";
import { IRestLayerRequest } from "@gtmhub/core";
import { EnvironmentService } from "@gtmhub/env";
import { IdMap } from "@gtmhub/util";
import { ICollection } from "@webapp/core/core.models";
import {
  ICreateKpiSnapshotPayload,
  IGetKpiSnapshotsQueryParams,
  IGetKpisQueryParams,
  IKpi,
  IKpiGroup,
  IKpiIdSnapshotsMap,
  IModifiableKpi,
  INewKpiGroup,
} from "../models/models";

export class KpiService {
  public static $inject = ["$http", "EnvironmentService"];

  constructor(private $http: IHttpService, private env: EnvironmentService) {}

  private setAllMissingFormatting(kpis: ICollection<IKpi>): ICollection<IKpi> {
    for (const [k, kpi] of kpis.items.entries()) {
      kpis.items[k] = this.setMissingFormatting(kpi);
    }
    return kpis;
  }
  // used to denote that no formatting is to be applied
  private defaultNoFormat: -1;
  private setMissingFormatting(kpi: IKpi): IKpi {
    if (typeof kpi.formatting === undefined) {
      kpi.formatting = { fractionSize: this.defaultNoFormat, prefix: "", suffix: "" };
    }
    return kpi;
  }

  public createKpi(kpi: IModifiableKpi): IPromise<IKpi> {
    const url: string = this.env.getApiEndpoint("/kpis");

    return this.$http.post<IKpi>(url, kpi).then((response) => response.data);
  }

  public createKpiSnapshot(kpiId: string, payload: ICreateKpiSnapshotPayload): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/kpis/${kpiId}/snapshots`);

    return this.$http.post<unknown>(url, payload);
  }

  public deleteKpi(kpiId: string): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/kpis/${kpiId}`);

    return this.$http.delete<unknown>(url);
  }

  public getKpi(kpiId: string): IPromise<IKpi> {
    const url: string = this.env.getApiEndpoint(`/kpis/${kpiId}`);

    return this.$http.get<IKpi>(url).then((response) => {
      return this.setMissingFormatting(response.data);
    });
  }

  public getKpis(queryParams: IGetKpisQueryParams = null): IPromise<ICollection<IKpi>> {
    const url: string = this.env.getApiEndpoint("/kpis");
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<ICollection<IKpi>>(url, query).then((response) => {
      return this.setAllMissingFormatting(response.data);
    });
  }

  public getKpisV2(params: IRestLayerRequest = null): IPromise<ICollection<IKpi>> {
    const url: string = this.env.getApiEndpointV2("/kpis");
    const query: IRequestShortcutConfig = {
      params,
    };

    return this.$http.get<ICollection<IKpi>>(url, query).then((response) => {
      return this.setAllMissingFormatting(response.data);
    });
  }

  public getKpisSnapshots(queryParams: IGetKpiSnapshotsQueryParams): IPromise<IKpiIdSnapshotsMap> {
    const url: string = this.env.getApiEndpoint("/kpis/snapshots");
    const query: IRequestShortcutConfig = {
      params: queryParams,
    };

    return this.$http.get<IKpiIdSnapshotsMap>(url, query).then((response) => response.data);
  }

  public patchKpi(kpiId: string, kpi: IModifiableKpi): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/kpis/${kpiId}`);

    return this.$http.patch<unknown>(url, kpi);
  }

  public patchKpis(patchableKpis: IdMap<IModifiableKpi>): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/kpis`);
    return this.$http.patch<unknown>(url, { kpis: patchableKpis });
  }

  public createKpiGroup(kpiGroup: INewKpiGroup): IPromise<IKpiGroup> {
    const url: string = this.env.getApiEndpoint("/kpis/groups");

    return this.$http.post<IKpiGroup>(url, kpiGroup).then((response) => response.data);
  }

  public patchKpiGroup(groupId: string, kpiGroup: INewKpiGroup): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/kpis/groups/${groupId}`);

    return this.$http.patch<unknown>(url, kpiGroup);
  }

  public deleteKpiGroup(groupId: string): IPromise<void> {
    const url: string = this.env.getApiEndpoint(`/kpis/groups/${groupId}`);

    return this.$http.delete<void>(url).then((response) => response.data);
  }

  public getKpiGroups(): IPromise<ICollection<IKpiGroup>> {
    const url: string = this.env.getApiEndpoint("/kpis/groups");

    return this.$http.get<ICollection<IKpiGroup>>(url).then((response) => response.data);
  }

  public addKpiGroupToFavorites(groupId: string): IPromise<void> {
    const url: string = this.env.getApiEndpoint(`/kpis/groups/${groupId}/favorite`);

    return this.$http.put<void>(url, null).then((response) => response.data);
  }

  public removeKpiGroupFromFavorites(groupId: string): IPromise<void> {
    const url: string = this.env.getApiEndpoint(`/kpis/groups/${groupId}/favorite`);

    return this.$http.delete<void>(url).then((response) => response.data);
  }

  public patchFavoriteSystemGroup(payload: { groupId: string; favorite: boolean }): IPromise<unknown> {
    const url: string = this.env.getApiEndpoint(`/users/kpi-virtual-group/favorite`);

    return this.$http.patch<unknown>(url, payload);
  }
}
